import { z } from 'zod';

/**
 * The fulfillment queue is an integer `donations.queue_position` per campaign
 * period, and physical grouping is **derived** from `campaigns.units_per_group`
 * at read time. There is no `sira` table and no `grup` table: a group that is
 * only ever a window over an order cannot fall out of step with the order —
 * see docs/decisions/0018-generic-fulfillment-queue.md and
 * docs/decisions/0025-queue-allocation-and-derived-grouping.md.
 */
export type QueuedDonation = {
  id: string;
  queuePosition: number;
  quantity: number | null;
  /**
   * What the shares of one group must agree on — `queueGroupKey` over the yurt
   * and the campaign's named answers. A change of key along the order ends the
   * group being filled, so no group ever holds two keys. Null or absent on
   * every row, which is what a campaign naming no grouping answer gives, is one
   * key throughout: the plain window of 0025.
   */
  groupKey?: string | null;
  /**
   * How many shares make this share's animal, when its answer to the
   * campaign's group size field names one — `groupSizeOf`. Null or absent is
   * the campaign's `units_per_group`. The size field is one of the grouping
   * keys, so every share of one run agrees on it.
   */
  groupSize?: number | null;
};

export type GroupShare = {
  group: number;
  shares: number;
  /** How many shares make this group's animal — its run's size. */
  size: number;
};

export type GroupedDonation = {
  id: string;
  queuePosition: number;
  shares: number;
  sharesBefore: number;
  /** The one-based group a human reads, or null when the campaign has no grouping. */
  groupFrom: number | null;
  groupTo: number | null;
  groupShares: readonly GroupShare[];
};

/**
 * An animal carried out and recorded — `fulfilled_queue_groups` with its
 * `fulfilled_queue_group_shares`. Its number, its size and the shares each
 * donation had in it are the record's, never re-derived: a recorded animal is a
 * **fixed window** every reader takes from the rows and the derivation flows
 * around (docs/decisions/0025-queue-allocation-and-derived-grouping.md, amended
 * a fourth time 2026-09-23). `shares` is the animal's own count, which stays
 * whole even when a donation that held some of them has since left the queue.
 */
export type RecordedAnimal = {
  group: number;
  size: number;
  shares: number;
  members: readonly { donationId: string; shares: number }[];
};

const sharesOf = (donation: QueuedDonation): number =>
  donation.quantity === null ? 1 : donation.quantity;

const byGroup = (left: GroupShare, right: GroupShare): number => left.group - right.group;

/**
 * The numbers derived groups take, in order: every positive number no recorded
 * animal holds, lowest first. With nothing recorded it is 1, 2, 3 …; in an
 * order nothing has disturbed since an animal was recorded it is exactly the
 * numbers the unrecorded groups already had, so recording renumbers nothing.
 */
function freeGroupNumbers(recorded: readonly RecordedAnimal[]): (ordinal: number) => number {
  const taken = new Set(recorded.map((animal) => animal.group));
  const numbers: number[] = [];
  let candidate = 0;
  return (ordinal) => {
    while (numbers.length <= ordinal) {
      candidate += 1;
      if (!taken.has(candidate)) numbers.push(candidate);
    }
    return numbers[ordinal] as number;
  };
}

/** Each donation's shares in the animals recorded, by donation, as the record holds them. */
export function recordedSharesOf(recorded: readonly RecordedAnimal[]): Map<string, GroupShare[]> {
  const byDonation = new Map<string, GroupShare[]>();
  for (const animal of recorded) {
    for (const member of animal.members) {
      const shares = byDonation.get(member.donationId) ?? [];
      shares.push({ group: animal.group, shares: member.shares, size: animal.size });
      byDonation.set(member.donationId, shares);
    }
  }
  return byDonation;
}

const byQueuePosition = (left: QueuedDonation, right: QueuedDonation): number =>
  left.queuePosition - right.queuePosition;

/**
 * The shares one animal of this share holds: its answer's own size, or the
 * campaign's. A size of 1 makes every share its own animal — a sheep.
 */
const sizeOf = (donation: QueuedDonation, unitsPerGroup: number): number =>
  donation.groupSize !== undefined && donation.groupSize !== null && donation.groupSize > 0
    ? donation.groupSize
    : unitsPerGroup;

/**
 * Group *n* of a period is the shares whose cumulative quantity falls in
 * `[n × size, (n + 1) × size)` along `queue_position`, over the live donations
 * the caller passes in — **counted afresh at every change of `groupKey`**, the
 * size being the run's own: its shares' `groupSize`, or `unitsPerGroup` for a
 * share whose answer names none (docs/decisions/0072). A run of rows agreeing on the key is cut into groups exactly as
 * the whole order used to be; where the key changes, the group being filled
 * ends, short if it must, and the next row opens a new one. With one key
 * throughout, which is every campaign naming no grouping answer, that is the
 * window of docs/decisions/0025-queue-allocation-and-derived-grouping.md and
 * nothing else — the amendment of 2026-09-23 says why a short group is the
 * price of never mixing a camel share into a cow.
 *
 * **A recorded animal is not derived.** Its number and the shares each
 * donation had in it come from `recorded` as they are, and only the shares no
 * animal holds — each donation's quantity less what its animals recorded —
 * flow through the windows above. A donation whose every share is recorded
 * still ends the run it stands in, so recording an animal never merges the runs
 * on either side of it. Derived groups take the numbers no recorded animal
 * holds, lowest first. A dequeue, a cancellation, a correction or a share
 * queued later therefore closes up or extends the derived groups and never
 * touches a recorded one — the fourth amendment of 0025.
 *
 * Cancelling a share leaves a gap in the positions and the derived groups close
 * up by one, which is what an operator does on paper and is why nothing has to
 * be repaired. A donation may straddle two groups of its own run; that is
 * reported rather than prevented, with `groupFrom`, `groupTo` and the share
 * count in each. Preventing it needs padding, which throws away shares that
 * were paid for.
 */
export function deriveGroups(
  donations: readonly QueuedDonation[],
  unitsPerGroup: number | null,
  recorded: readonly RecordedAnimal[] = [],
): readonly GroupedDonation[] {
  const ordered = [...donations].sort(byQueuePosition);
  const grouping = unitsPerGroup !== null && unitsPerGroup > 0 ? unitsPerGroup : null;
  const recordedShares = recordedSharesOf(recorded);
  const numberOf = freeGroupNumbers(recorded);
  let sharesBefore = 0;
  let groupsClosed = 0;
  let runShares = 0;
  let runKey: string | null = null;
  let runSize = grouping ?? 1;

  return ordered.map((donation, index) => {
    const shares = sharesOf(donation);
    const start = sharesBefore;
    sharesBefore += shares;

    if (grouping === null) {
      return {
        id: donation.id,
        queuePosition: donation.queuePosition,
        shares,
        sharesBefore: start,
        groupFrom: null,
        groupTo: null,
        groupShares: [],
      };
    }

    const key = donation.groupKey ?? null;
    if (index === 0 || key !== runKey) {
      if (index > 0) groupsClosed += Math.ceil(runShares / runSize);
      runShares = 0;
      runSize = sizeOf(donation, grouping);
    }
    runKey = key;

    const held = recordedShares.get(donation.id) ?? [];
    const flowing = Math.max(0, shares - held.reduce((sum, share) => sum + share.shares, 0));
    const runStart = runShares;
    runShares += flowing;

    const groupShares: GroupShare[] = [...held];
    if (flowing > 0) {
      const firstInRun = Math.floor(runStart / runSize);
      const lastInRun = Math.floor((runStart + flowing - 1) / runSize);
      for (let inRun = firstInRun; inRun <= lastInRun; inRun += 1) {
        const groupStart = inRun * runSize;
        const overlapStart = Math.max(runStart, groupStart);
        const overlapEnd = Math.min(runStart + flowing, groupStart + runSize);
        groupShares.push({
          group: numberOf(groupsClosed + inRun),
          shares: overlapEnd - overlapStart,
          size: runSize,
        });
      }
    }
    groupShares.sort(byGroup);

    return {
      id: donation.id,
      queuePosition: donation.queuePosition,
      shares,
      sharesBefore: start,
      groupFrom: groupShares[0]?.group ?? null,
      groupTo: groupShares.at(-1)?.group ?? null,
      groupShares,
    };
  });
}

/**
 * Each group's number, how many shares it holds and how many make it whole, in
 * order. A group holding fewer than its size is an animal not yet full — the
 * last of a run, which the next share of the same answers queued will fill, or
 * which the operator's *Grupla* merges with another run of the same answers.
 * A recorded animal is counted from its record, so it keeps the shares it was
 * carried out with even after a donation holding some of them left the queue.
 */
export function groupSizes(
  grouped: readonly GroupedDonation[],
  recorded: readonly RecordedAnimal[] = [],
): readonly GroupShare[] {
  const sizes = new Map<number, GroupShare>();
  for (const row of grouped) {
    for (const share of row.groupShares) {
      const held = sizes.get(share.group);
      sizes.set(share.group, {
        group: share.group,
        shares: (held?.shares ?? 0) + share.shares,
        size: share.size,
      });
    }
  }
  for (const animal of recorded) {
    sizes.set(animal.group, { group: animal.group, shares: animal.shares, size: animal.size });
  }
  return [...sizes.values()].sort(byGroup);
}

/** The groups holding fewer shares than make their animal. */
export function shortGroupsOf(
  grouped: readonly GroupedDonation[],
  recorded: readonly RecordedAnimal[] = [],
): number[] {
  return groupSizes(grouped, recorded)
    .filter((group) => group.shares < group.size)
    .map((group) => group.group);
}

/**
 * The groups each donation's own shares were recorded in when an animal was
 * carried out — `fulfilled_queue_group_shares`, by donation. A group is
 * recorded once, whole, by `donation.fulfillGroup`.
 */
export type RecordedGroups = ReadonlyMap<string, ReadonlySet<number>>;

/** Which animals recorded each donation's shares, by donation. */
export function recordedGroupsOf(recorded: readonly RecordedAnimal[]): RecordedGroups {
  const byDonation = new Map<string, Set<number>>();
  for (const animal of recorded) {
    for (const member of animal.members) {
      const groups = byDonation.get(member.donationId) ?? new Set<number>();
      groups.add(animal.group);
      byDonation.set(member.donationId, groups);
    }
  }
  return byDonation;
}

/**
 * How many of the animals a donation's shares fall in have been carried out
 * with its shares in them, out of how many it touches. A donation that
 * touches no group — a campaign that is not shares — has none.
 */
export function animalsFulfilled(
  groupShares: readonly GroupShare[],
  recorded: ReadonlySet<number> | undefined,
): { fulfilled: number; total: number } | null {
  if (groupShares.length === 0) return null;
  const fulfilled = groupShares.filter((share) => recorded?.has(share.group) ?? false).length;
  return { fulfilled, total: groupShares.length };
}

/**
 * The donations recording `group` completes: those holding a share in it
 * whose every **other** group is already recorded with their shares in it. A
 * donation becomes `fulfilled` with the last animal its shares fall in, never
 * the first — docs/decisions/0025-queue-allocation-and-derived-grouping.md,
 * its third amendment of 2026-09-23. A sheep donation of three shares is three
 * animals and completes with the third; a seven-share donation straddling two
 * cattle completes with the second.
 */
export function donationsCompletedBy(
  grouped: readonly GroupedDonation[],
  group: number,
  recorded: RecordedGroups,
): string[] {
  return grouped
    .filter((row) => row.groupShares.some((share) => share.group === group))
    .filter((row) =>
      row.groupShares.every(
        (share) => share.group === group || (recorded.get(row.id)?.has(share.group) ?? false),
      ),
    )
    .map((row) => row.id);
}

/**
 * The legacy's descending first-fit, run over the current live shares and
 * returned as a **permutation** of the rows rather than as a set of bins. It
 * minimises straddling; nothing is created, numbered, kept in step with
 * `units_per_group`, or repaired when a share is cancelled.
 *
 * Rows are never added, dropped or altered — the same objects come back in a
 * new order.
 */
export function compactOrder<T extends QueuedDonation>(
  donations: readonly T[],
  unitsPerGroup: number | null,
): readonly T[] {
  if (unitsPerGroup === null || unitsPerGroup <= 0) return [...donations].sort(byQueuePosition);

  const descending = [...donations].sort(
    (left, right) => sharesOf(right) - sharesOf(left) || byQueuePosition(left, right),
  );

  const bins: { size: number; remaining: number; donations: T[] }[] = [];
  for (const donation of descending) {
    const shares = sharesOf(donation);
    const size = sizeOf(donation, unitsPerGroup);
    const bin = bins.find((candidate) => candidate.size === size && candidate.remaining >= shares);
    if (bin) {
      bin.remaining -= shares;
      bin.donations.push(donation);
    } else {
      bins.push({ size, remaining: Math.max(0, size - shares), donations: [donation] });
    }
  }

  return bins.flatMap((bin) => bin.donations);
}

/**
 * The operator's *Grupla*: the rows gathered by `groupKey` — each key's rows
 * together, the keys in the order their first row already stands — and each
 * gathering packed by `compactOrder`, the legacy's descending first-fit. It is
 * a **permutation** like the compaction it composes with: nothing is created,
 * numbered, dropped or reported unassignable, and with one key throughout it
 * is `compactOrder` exactly. Under `deriveGroups` no group then holds two
 * keys, and each key leaves at most the animals its first-fit could not fill.
 */
export function groupedOrder<T extends QueuedDonation>(
  donations: readonly T[],
  unitsPerGroup: number | null,
): readonly T[] {
  const gatherings = new Map<string | null, T[]>();
  for (const donation of [...donations].sort(byQueuePosition)) {
    const key = donation.groupKey ?? null;
    const gathering = gatherings.get(key);
    if (gathering) gathering.push(donation);
    else gatherings.set(key, [donation]);
  }
  return [...gatherings.values()].flatMap((gathering) => compactOrder(gathering, unitsPerGroup));
}

/**
 * The positions one `campaign_period_queue_counters` allocation hands out.
 * The counter statement returns the first position and advances by `count` in
 * the same round trip, so a bulk queueing of three hundred donations takes one
 * statement and one row lock.
 */
export function allocatedQueuePositions(firstPosition: number, count: number): readonly number[] {
  if (count <= 0) return [];
  return Array.from({ length: count }, (_unused, offset) => firstPosition + offset);
}

/**
 * What `next_position` must be left at after a reorder has rewritten a
 * period's positions: one past the highest written position, and never below
 * one for a period whose queue is empty.
 */
export function nextQueueCounterPosition(positions: readonly number[]): number {
  return positions.reduce((highest, position) => Math.max(highest, position), 0) + 1;
}

/**
 * The fixed sort keys `donation.queueSort` accepts. Ordering by the institution
 * that will carry the work out and then by arrival is what makes a derived
 * group belong to one place, which is what the legacy's five-level cascading
 * top-up was reaching for — docs/decisions/0025-queue-allocation-and-derived-grouping.md.
 */
export const QUEUE_SORT_KEYS = ['arrival', 'fulfillment_institution', 'country', 'zone'] as const;
export type QueueFixedSortKey = (typeof QUEUE_SORT_KEYS)[number];

/**
 * A sort by one of the campaign's own grouping answers, named by its field
 * key: `answer:video_requested`. Which keys a period may be sorted by is its
 * campaign's `group_by_field_keys`, so the contract accepts any field key's
 * shape and the service refuses one the campaign does not name — a fixed enum
 * of form fields here would be a campaign's identity written into the code.
 */
export const QUEUE_ANSWER_SORT_PREFIX = 'answer:';
export type QueueAnswerSortKey = `${typeof QUEUE_ANSWER_SORT_PREFIX}${string}`;
export type QueueSortKey = QueueFixedSortKey | QueueAnswerSortKey;

const answerSortKeySchema = z
  .string()
  .regex(/^answer:[a-z][a-z0-9_]{0,39}$/)
  .transform((value) => value as QueueAnswerSortKey);

export const queueSortKeySchema = z.union([z.enum(QUEUE_SORT_KEYS), answerSortKeySchema]);

export function queueAnswerSortKey(fieldKey: string): QueueAnswerSortKey {
  return `${QUEUE_ANSWER_SORT_PREFIX}${fieldKey}`;
}

/** The field key an answer sort names, or null for one of the fixed keys. */
export function answerSortField(sortKey: QueueSortKey): string | null {
  return sortKey.startsWith(QUEUE_ANSWER_SORT_PREFIX)
    ? sortKey.slice(QUEUE_ANSWER_SORT_PREFIX.length)
    : null;
}

/**
 * How many groups a period holds: every derived group its live shares fall
 * into and every animal recorded. It counts them rather than reading the
 * highest number, because a derived group closing up behind a recorded animal
 * leaves a number nobody holds. A period with no grouping has no groups at all
 * rather than one flat group, because the screen that reads this draws a
 * separator per group and a campaign that is not shares of an animal draws none.
 */
export function groupCountOf(
  grouped: readonly GroupedDonation[],
  recorded: readonly RecordedAnimal[] = [],
): number {
  return groupSizes(grouped, recorded).length;
}
